/**
 * Tab-to-tab sync via BroadcastChannel — when the end-user has the partner
 * site open in two tabs and fires an event in tab A, tab B's widgets update
 * instantly without waiting for the next smart-poll tick.
 *
 * Codex review fix: channel name and message payload are scoped to the
 * (apiBaseUrl + widget token) pair. Two different tenants embedded under the
 * same origin (e.g. a marketplace platform showing widgets for two different
 * Hatched customers) used to share `hatched-sdk-v1` and bleed state across
 * tokens. Each token now derives its own channel; mismatched messages on a
 * stale subscription are dropped defensively.
 *
 * We still tag every outbound message with a per-tab origin id so the sending
 * tab can ignore its own echo.
 *
 * Falls back to a no-op on browsers without BroadcastChannel (Safari < 15.4
 * historically). Widgets still update via their own store writes; cross-tab
 * sync simply doesn't happen.
 */
import type { WorkspaceStore, EventEffects, WorkspaceState } from './store';

const CHANNEL_PREFIX = 'hatched-sdk-v1';

type SyncMessage =
  | { kind: 'effects'; origin: string; scope: string; effects: EventEffects }
  | { kind: 'hydrate'; origin: string; scope: string; patch: Partial<WorkspaceState> };

function randomTabId(): string {
  // crypto.randomUUID is available everywhere we care about in 2026, but we
  // guard anyway — some embedded WebViews still lag.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Hash a (token + apiBase) tuple into a short opaque scope tag. We don't put
 * the raw token on the channel name — the browser exposes channel names in
 * dev-tools, and tokens are sensitive. A short cyrb53-style mix is enough to
 * partition tenants while staying readable in logs.
 */
function hashScope(apiBaseUrl: string, token: string): string {
  const input = `${apiBaseUrl}|${token}`;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(12, '0');
}

export interface CrossTabBridge {
  broadcastEffects: (effects: EventEffects) => void;
  broadcastHydrate: (patch: Partial<WorkspaceState>) => void;
  close: () => void;
}

export interface CrossTabBridgeScope {
  apiBaseUrl: string;
  token: string;
}

export function attachCrossTabBridge(
  store: WorkspaceStore,
  scope?: CrossTabBridgeScope,
): CrossTabBridge {
  if (typeof BroadcastChannel === 'undefined') {
    return {
      broadcastEffects: () => {},
      broadcastHydrate: () => {},
      close: () => {},
    };
  }

  const scopeTag = scope ? hashScope(scope.apiBaseUrl, scope.token) : 'shared';
  const tabId = randomTabId();
  const channelName = `${CHANNEL_PREFIX}:${scopeTag}`;
  const channel = new BroadcastChannel(channelName);

  channel.onmessage = (event: MessageEvent<SyncMessage>) => {
    const msg = event.data;
    if (!msg || msg.origin === tabId) return;
    // Defensive — a stale subscription on a previously-closed token must not
    // apply messages from a different scope even if the browser somehow
    // delivered them (e.g. legacy channels still buffered).
    if (msg.scope !== scopeTag) return;
    if (msg.kind === 'effects') {
      store.applyEffects(msg.effects);
    } else if (msg.kind === 'hydrate') {
      store.hydrate(msg.patch);
    }
  };

  return {
    broadcastEffects(effects) {
      channel.postMessage({ kind: 'effects', origin: tabId, scope: scopeTag, effects });
    },
    broadcastHydrate(patch) {
      channel.postMessage({ kind: 'hydrate', origin: tabId, scope: scopeTag, patch });
    },
    close() {
      channel.close();
    },
  };
}
